/**
 * Export and import. FR-601..FR-603, FR-606, FR-006, L-10.
 * Format specified in docs/11-data-model.md Section 5.
 *
 * ## The export IS the interchange format
 *
 * Documented publicly so a third party can write an importer from the spec
 * alone. Charter goal G3 — the user can leave with all their data — is not
 * satisfied by a proprietary blob only this app can read.
 *
 * ## Two rules that are absolute
 *
 * **No credential ever appears in an export.** Not the PIN, not the derived key,
 * not the wrapped data key, not the device id. An export is the artifact most
 * likely to end up in a cloud backup or a Downloads folder, and shipping the key
 * alongside the ciphertext would make the encryption pointless.
 *
 * **Byte-identical for identical data.** Stable ordering, no timestamps in the
 * body. A user who wants to diff two exports and verify nothing changed can.
 * That is a trust feature, not a nicety.
 */
import type { DayEntry } from '@domain/types'
import { open as openSealed, seal, type SealedBlob } from './crypto'
import { newSalt, toBase64, fromBase64, currentIterations } from './crypto'
import { getAllEntries, putEntry } from './entryStore'
import { loadSettings, saveSettings, type Settings } from './settingsStore'
import { loadSymptoms, putSymptom, type SymptomDef } from './symptoms'

export const EXPORT_FORMAT = 'thana-export'
export const EXPORT_FORMAT_VERSION = 1

export interface ThanaExport {
  format: typeof EXPORT_FORMAT
  formatVersion: number
  /** ISO instant. The one place a timestamp appears; excluded from comparisons. */
  exportedAt: string
  app: { name: string; version: string }
  schema: { entry: number; settings: number }
  data: {
    entries: DayEntry[]
    symptoms: SymptomDef[]
    settings: Settings
  }
}

/** An encrypted export. The inner document is exactly the plaintext format. */
export interface EncryptedExport {
  format: typeof EXPORT_FORMAT
  formatVersion: number
  encryption: {
    algorithm: 'AES-GCM-256'
    kdf: 'PBKDF2-SHA256'
    iterations: number
    salt: string
  }
  sealed: SealedBlob
}

const sortByDate = (a: DayEntry, b: DayEntry) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)

/**
 * Build the export document.
 *
 * `settings` is included minus anything sensitive. Note what is stripped and
 * why: the lock configuration would tell a reader that a PIN exists, and sync
 * credentials (from v0.5) are credentials.
 */
export async function buildExport(appVersion: string, now: Date): Promise<ThanaExport> {
  const [entries, symptoms, settings] = await Promise.all([
    getAllEntries(),
    loadSymptoms(),
    loadSettings(),
  ])

  return {
    format: EXPORT_FORMAT,
    formatVersion: EXPORT_FORMAT_VERSION,
    exportedAt: now.toISOString(),
    app: { name: 'Thana', version: appVersion },
    schema: { entry: 1, settings: settings.v },
    data: {
      // Sorted so two exports of the same data are byte-identical and diffable.
      entries: [...entries].sort(sortByDate),
      symptoms: [...symptoms].sort((a, b) => a.order - b.order),
      settings,
    },
  }
}

export function serialise(document: ThanaExport): string {
  return JSON.stringify(document, null, 2)
}

/**
 * Encrypt an export under a passphrase the user supplies.
 *
 * A fresh salt every time, and its own passphrase — deliberately NOT the app
 * PIN. Reusing the PIN would mean a leaked export weakened the device, and it
 * would tie the lifetime of a backup to a secret the user may later change.
 */
export async function encryptExport(
  document: ThanaExport,
  passphrase: string,
): Promise<EncryptedExport> {
  const salt = newSalt()
  const iterations = currentIterations()
  const key = await deriveKekForExport(passphrase, salt, iterations)

  return {
    format: EXPORT_FORMAT,
    formatVersion: EXPORT_FORMAT_VERSION,
    encryption: {
      algorithm: 'AES-GCM-256',
      kdf: 'PBKDF2-SHA256',
      iterations,
      salt: toBase64(salt),
    },
    sealed: await seal(document, key),
  }
}

export async function decryptExport(
  file: EncryptedExport,
  passphrase: string,
): Promise<ThanaExport> {
  const key = await deriveKekForExport(
    passphrase,
    fromBase64(file.encryption.salt),
    file.encryption.iterations,
  )
  // Throws on a wrong passphrase: AES-GCM authenticates, so there is no path
  // where a wrong key yields plausible-looking wrong data.
  return openSealed<ThanaExport>(file.sealed, key)
}

/**
 * Derivation for exports.
 *
 * Deliberately separate from `deriveKek` in crypto.ts: that one produces a
 * wrap/unwrap key for the keyring, whereas an export needs encrypt/decrypt
 * usages. WebCrypto enforces the distinction, and getting it wrong fails at
 * runtime rather than at compile time — so the two live apart with their reasons
 * written down.
 */
async function deriveKekForExport(
  passphrase: string,
  salt: Uint8Array,
  iterations: number,
): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as unknown as BufferSource, iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

export const isEncryptedExport = (value: unknown): value is EncryptedExport =>
  typeof value === 'object' &&
  value !== null &&
  'encryption' in value &&
  'sealed' in value

export interface ImportPreview {
  document: ThanaExport
  newEntries: number
  changedEntries: number
  unchangedEntries: number
  symptoms: number
}

/**
 * Parse and validate without applying anything. FR-606.
 *
 * The preview exists because import is the one operation that can destroy data a
 * user cannot get back. Showing the counts first turns "I hope this is the right
 * file" into a decision.
 */
export async function previewImport(raw: string): Promise<ImportPreview> {
  const parsed: unknown = JSON.parse(raw)
  const document = assertExport(parsed)

  const existing = new Map((await getAllEntries()).map((e) => [e.date, e]))

  let newEntries = 0
  let changedEntries = 0
  let unchangedEntries = 0

  for (const entry of document.data.entries) {
    const current = existing.get(entry.date)
    if (current === undefined) newEntries++
    else if (JSON.stringify(stripMeta(current)) === JSON.stringify(stripMeta(entry)))
      unchangedEntries++
    else changedEntries++
  }

  return {
    document,
    newEntries,
    changedEntries,
    unchangedEntries,
    symptoms: document.data.symptoms.length,
  }
}

/** Meta carries write timestamps, which differ between devices for identical data. */
function stripMeta(entry: DayEntry): Omit<DayEntry, 'meta'> {
  const { meta: _meta, ...rest } = entry
  return rest
}

function assertExport(value: unknown): ThanaExport {
  if (typeof value !== 'object' || value === null) throw new Error('Not a Thana export')
  const candidate = value as Partial<ThanaExport>

  if (candidate.format !== EXPORT_FORMAT) throw new Error('Not a Thana export')
  if (typeof candidate.formatVersion !== 'number') throw new Error('Missing format version')
  if (candidate.formatVersion > EXPORT_FORMAT_VERSION) {
    // Forward-only, like the database (data model Section 4). Refusing clearly
    // beats importing a shape this build does not understand and losing fields.
    throw new Error('This file was written by a newer version of Thana')
  }
  if (typeof candidate.data !== 'object' || candidate.data === null) {
    throw new Error('Export contains no data')
  }
  if (!Array.isArray(candidate.data.entries)) throw new Error('Export contains no entries')

  return candidate as ThanaExport
}

export type ImportMode = 'merge' | 'replace'

/**
 * Apply a previewed import.
 *
 * `merge` keeps whichever version of a day was updated later; `replace` takes
 * the file as authoritative for every day it mentions. Neither touches days the
 * file does not mention — a "replace" that silently deleted unmentioned days
 * would be a data-loss trap.
 */
export async function applyImport(
  preview: ImportPreview,
  mode: ImportMode,
): Promise<{ applied: number }> {
  const existing = new Map((await getAllEntries()).map((e) => [e.date, e]))
  let applied = 0

  for (const entry of preview.document.data.entries) {
    const current = existing.get(entry.date)
    if (mode === 'merge' && current !== undefined) {
      if (current.meta.updatedAt >= entry.meta.updatedAt) continue
    }

    const { v: _v, date, meta: _meta, ...fields } = entry
    await putEntry(date, fields)
    applied++
  }

  for (const symptom of preview.document.data.symptoms) {
    await putSymptom(symptom)
  }

  // Lock configuration is never in an export, so importing settings cannot
  // change how the app is protected.
  await saveSettings(preview.document.data.settings)

  return { applied }
}
